import React, { memo, useEffect, useMemo, useState } from "react";
import { MathJax } from "better-react-mathjax";
import { Button } from "@material-tailwind/react";
import useAuthStore from "../../../store/authStore";

const removeExtraClosingToken = (source, openToken, closeToken) => {
  let text = String(source || "");
  let result = "";
  let index = 0;
  let balance = 0;

  while (index < text.length) {
    if (text.startsWith(openToken, index)) {
      balance += 1;
      result += openToken;
      index += openToken.length;
      continue;
    }

    if (text.startsWith(closeToken, index)) {
      if (balance > 0) {
        balance -= 1;
        result += closeToken;
      }

      index += closeToken.length;
      continue;
    }

    result += text[index];
    index += 1;
  }

  if (balance > 0) {
    result += closeToken.repeat(balance);
  }

  return result;
};

const countUnescapedDollarSigns = (value) => {
  const text = String(value || "");
  let count = 0;

  for (let index = 0; index < text.length; index += 1) {
    if (text[index] !== "$") continue;

    let slashCount = 0;
    let cursor = index - 1;

    while (cursor >= 0 && text[cursor] === "\\") {
      slashCount += 1;
      cursor -= 1;
    }

    if (slashCount % 2 === 0) {
      count += 1;
    }
  }

  return count;
};

const isEscapedLatexAt = (text, index) => {
  let slashCount = 0;
  let cursor = index - 1;

  while (cursor >= 0 && text[cursor] === "\\") {
    slashCount += 1;
    cursor -= 1;
  }

  return slashCount % 2 === 1;
};

const balanceLatexCharacterPair = (value, openCharacter, closeCharacter) => {
  const text = String(value || "");
  let result = "";
  let balance = 0;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];

    if (character === openCharacter && !isEscapedLatexAt(text, index)) {
      balance += 1;
      result += character;
      continue;
    }

    if (character === closeCharacter && !isEscapedLatexAt(text, index)) {
      if (balance > 0) {
        balance -= 1;
        result += character;
      }
      continue;
    }

    result += character;
  }

  return result + closeCharacter.repeat(balance);
};

const repairLatexMathBody = (value) => {
  let body = String(value || "")
    .replace(/\\(?:righ|rig|ri|r)(?=\s*$)/g, "")
    .replace(/\\left(?![A-Za-z])\s*/g, "")
    .replace(/\\right(?![A-Za-z])\s*/g, "");

  body = balanceLatexCharacterPair(body, "{", "}");
  body = balanceLatexCharacterPair(body, "[", "]");
  body = balanceLatexCharacterPair(body, "(", ")");

  return body.trim();
};

const repairLatexDelimitedBodies = (value) => {
  let text = String(value || "");

  text = text.replace(
    /\$\$([\s\S]*?)\$\$/g,
    (full, body) => `$$${repairLatexMathBody(body)}$$`,
  );

  text = text.replace(
    /\\\[([\s\S]*?)\\\]/g,
    (full, body) => `\\[${repairLatexMathBody(body)}\\]`,
  );

  text = text.replace(
    /\\\(([\s\S]*?)\\\)/g,
    (full, body) => `\\(${repairLatexMathBody(body)}\\)`,
  );

  text = text.replace(
    /\$([^$\n]*?)\$/g,
    (full, body) => `$${repairLatexMathBody(body)}$`,
  );

  return text;
};

const repairMalformedLatex = (value) => {
  let text = String(value || "");

  if (!text) return "";

  text = text
    .replace(/\\mathmr\b/g, "\\mathrm")
    .replace(/\\rm\s*\{/g, "\\mathrm{")
    .replace(
      /(?:Extra\s+\\left\s+or\s+missing\s+\\right|Missing\s+\\right|Extra\s+\\right)/gi,
      "",
    )
    .replace(/\\left(?![A-Za-z])\s*/g, "")
    .replace(/\\right(?![A-Za-z])\s*/g, "")
    .replace(/\\(?:righ|rig|ri|r|lef|le|l)(?=\s*(?:\$|\\\)|\\\]|<|$))/g, "");

  text = removeExtraClosingToken(text, "\\(", "\\)");
  text = removeExtraClosingToken(text, "\\[", "\\]");

  if (countUnescapedDollarSigns(text) % 2 !== 0) {
    text += "$";
  }

  text = repairLatexDelimitedBodies(text);

  return text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
};
const PRINTED_CHOICE_LABEL_REGEX =
  /^\s*(?:\(\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\)|\[\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\]|(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*[\).,:;\-])\s*/i;

const collapseDuplicateOptionLabel = (value) => {
  const text = String(value || "").trim();
  const label =
    "(?:\\(\\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*\\)|" +
    "\\[\\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*\\]|" +
    "(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*[\\).,:;\\-])";
  const match = text.match(
    new RegExp(`^\\s*(${label})\\s+(${label})\\s+`, "i"),
  );

  if (!match) return text;

  const first = String(match[1] || "")
    .replace(/\s+/g, "")
    .toLowerCase();
  const second = String(match[2] || "")
    .replace(/\s+/g, "")
    .toLowerCase();

  return first === second
    ? `${match[1]} ${text.slice(match[0].length)}`.trim()
    : text;
};

const normalizeStatus = (status) => {
  if (status === "Ok") return "Ok";
  if (status === "Error") return "Error";
  return "Unseen";
};

const getNextStatus = (status) => {
  if (status === "Unseen") return "Ok";
  if (status === "Ok") return "Error";
  return "Unseen";
};

const getButtonText = (status) => {
  return status === "Unseen" ? "Update" : status;
};

const getQuestionId = (question) => {
  const value = question?.id || question?._id || "";
  return value ? String(value) : "";
};

const OCR_SUPERSCRIPT_MAP = {
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
  "⁺": "+",
  "⁻": "-",
};

const replaceOcrSuperscripts = (value) => {
  return String(value || "").replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻]+/g, (sequence) => {
    const converted = Array.from(sequence)
      .map((character) => OCR_SUPERSCRIPT_MAP[character] || "")
      .join("");

    return converted ? `^{${converted}}` : sequence;
  });
};

const prefixPlainMathCommand = (value, command) => {
  const pattern = new RegExp(`\\b${command}\\b`, "gi");

  return String(value || "").replace(pattern, (match, offset, source) => {
    const previousCharacter = offset > 0 ? source[offset - 1] : "";

    return previousCharacter === "\\"
      ? match
      : `\\${String(match).toLowerCase()}`;
  });
};

const replacePlainGreekSymbols = (value) => {
  const greekMap = {
    μ: "\\mu",
    α: "\\alpha",
    β: "\\beta",
    γ: "\\gamma",
    δ: "\\delta",
    θ: "\\theta",
    φ: "\\phi",
    π: "\\pi",
    ω: "\\omega",
    λ: "\\lambda",
    ρ: "\\rho",
    σ: "\\sigma",
    Δ: "\\Delta",
  };

  return String(value || "").replace(
    /[μαβγδθφπωλρσΔ]/g,
    (symbol) => greekMap[symbol] || symbol,
  );
};

const normalizePlainFractionsToLatex = (value) => {
  let text = String(value || "");

  text = text.replace(
    /\(\s*([^()\n]+?)\s*\)\s*\/\s*(\\[A-Za-z]+|[A-Za-z0-9.]+)(?=\s*(?:\)|$|[,;]))/g,
    (match, numerator, denominator) =>
      `\\frac{${String(numerator).trim()}}{${String(denominator).trim()}}`,
  );

  text = text.replace(
    /(^|[\s(])((?:\\[A-Za-z]+|[A-Za-z0-9.]+))\s*\/\s*((?:\\[A-Za-z]+|[A-Za-z0-9.]+))(?=\s*(?:\)|$|[,;]))/g,
    (match, prefix, numerator, denominator) =>
      `${prefix}\\frac{${numerator}}{${denominator}}`,
  );

  return text;
};

const normalizePlainRootsToLatex = (value) => {
  let text = String(value || "");

  text = text.replace(
    /√\s*\(\s*([^()]+?)\s*\)/g,
    (match, radicand) => `\\sqrt{${String(radicand).trim()}}`,
  );

  text = text.replace(
    /√\s*([A-Za-z0-9]+|\\[A-Za-z]+)/g,
    (match, radicand) => `\\sqrt{${String(radicand).trim()}}`,
  );

  return text;
};

const wrapPlainFunctionArguments = (value) => {
  return String(value || "").replace(
    /(\\(?:sin|cos|tan|cot|sec|csc)(?:\^\{[^}]+\})?)\s*\(\s*([^()\n]+(?:\{[^{}]*\}[^()\n]*)*)\s*\)/g,
    (match, functionName, argument) =>
      `${functionName}\\left(${String(argument).trim()}\\right)`,
  );
};

const hasMathDelimiter = (value) => {
  const text = String(value || "");

  return (
    text.includes("\\(") ||
    text.includes("\\[") ||
    text.includes("$$") ||
    /(^|[^\\])\$[^$]+\$/.test(text)
  );
};

const looksLikeMath = (value) => {
  const text = String(value || "");

  return (
    /\\(?:frac|dfrac|tfrac|sqrt|sin|cos|tan|cot|sec|csc|log|ln|mu|alpha|beta|gamma|theta|phi|pi|omega|left|right)\b/.test(
      text,
    ) ||
    /\b(?:sin|cos|tan|cot|sec|csc|log|ln)\b/i.test(text) ||
    /[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻√μαβγδθφπωλρσΔ]/.test(text) ||
    /\^\s*(?:\{[^}]+\}|[-+]?\d+)/.test(text) ||
    /_\s*(?:\{[^}]+\}|[A-Za-z0-9])/.test(text) ||
    /(?:[A-Za-z0-9)]|\})\s*\/\s*(?:[A-Za-z0-9(]|\\)/.test(text)
  );
};

const normalizeOcrMathBodyToLatex = (value) => {
  let text = String(value || "")
    .replace(/&mu;/gi, "μ")
    .replace(/&alpha;/gi, "α")
    .replace(/&beta;/gi, "β")
    .replace(/&theta;/gi, "θ")
    .replace(/&pi;/gi, "π")
    .replace(/\u2212/g, "-")
    .replace(/[ \t]+/g, " ")
    .trim();

  if (!text || !looksLikeMath(text)) {
    return text;
  }

  text = replaceOcrSuperscripts(text);
  text = replacePlainGreekSymbols(text);
  text = normalizePlainRootsToLatex(text);

  ["sin", "cos", "tan", "cot", "sec", "csc", "log", "ln"].forEach((command) => {
    text = prefixPlainMathCommand(text, command);
  });

  text = normalizePlainFractionsToLatex(text);
  text = wrapPlainFunctionArguments(text);

  return text
    .replace(/\s+\)/g, ")")
    .replace(/\(\s+/g, "(")
    .replace(/\s+\}/g, "}")
    .replace(/\{\s+/g, "{")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const convertDisplayMathToInline = (value) => {
  return String(value || "")
    .replace(
      /\\\[\s*([\s\S]*?)\s*\\\]/g,
      (match, formula) => `\\(${String(formula || "").trim()}\\)`,
    )
    .replace(
      /\$\$\s*([\s\S]*?)\s*\$\$/g,
      (match, formula) => `\\(${String(formula || "").trim()}\\)`,
    );
};

const normalizeOptionForMathJax = (value) => {
  const rawSource = repairMalformedLatex(value);

  if (!rawSource) return "";

  const cleanedSource = collapseDuplicateOptionLabel(rawSource);
  const optionLabelMatch = cleanedSource.match(
    /^(\s*(?:\(\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\)|\[\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\]|(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*[\).,:;\-])\s*)([\s\S]*)$/i,
  );

  const label = optionLabelMatch ? optionLabelMatch[1] : "";
  let body = optionLabelMatch ? optionLabelMatch[2].trim() : cleanedSource;

  if (!body) return label.trim();

  body = convertDisplayMathToInline(body);

  if (hasMathDelimiter(body)) {
    return `${label}${body}`;
  }

  if (looksLikeMath(body)) {
    const normalizedBody = normalizeOcrMathBodyToLatex(body);

    return normalizedBody
      ? `${label}\\(${normalizedBody}\\)`
      : `${label}${body}`;
  }

  return `${label}${body}`;
};

const escapeHtmlAttribute = (value) => {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

const Question = memo(
  ({ question, i = null, qSetId = null, changeStatus = () => {} }) => {
    const [updateState, setUpdateState] = useState(
      normalizeStatus(question?.status),
    );

    const { client } = useAuthStore();

    useEffect(() => {
      setUpdateState(normalizeStatus(question?.status));
    }, [question?.status]);

    const imageBaseUrl = useMemo(() => {
      const s3Dir = String(client?.s3Dir || "").replace(/^\/+|\/+$/g, "");

      return `https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${qSetId}`;
    }, [client?.s3Dir, qSetId]);

    function renderTextWithTags(text, options = {}) {
      if (text === null || text === undefined) return "";

      let html = options.option
        ? normalizeOptionForMathJax(text)
        : repairMalformedLatex(text);

      const placeholders = [];
      const seenImages = new Set();

      const pushImage = (src, alt = "image") => {
        const finalSrc = String(src || "").trim();

        if (!finalSrc || seenImages.has(finalSrc)) {
          return "";
        }

        seenImages.add(finalSrc);

        const key = `@@QUESTION_IMAGE_${placeholders.length}@@`;

        placeholders.push(
          `<img src="${escapeHtmlAttribute(
            finalSrc,
          )}" alt="${escapeHtmlAttribute(
            alt,
          )}" class="inline-block max-w-full h-auto align-middle my-2" />`,
        );

        return key;
      };

      html = html.replace(
        /!\[([^\]]*)\]\s*\(\s*(https?:\/\/[^)\s]+?\.(?:png|jpg|jpeg|webp|gif)(?:\?[^)\s]*)?)\s*\)/gi,
        (match, alt, url) => pushImage(url, alt || "image"),
      );

      html = html.replace(
        /!\[([^\]]*)\]\s*\(\s*((?:solution-)?image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif))\s*\)/gi,
        (match, alt, imageName) =>
          pushImage(`${imageBaseUrl}/${imageName}`, alt || imageName),
      );

      /*
       * Full URL must be replaced before the filename regex. Otherwise only
       * "image-xxx.png" inside the URL becomes an <img>, leaving the URL prefix
       * visible as text.
       */
      html = html.replace(
        /https?:\/\/[^\s<>"')]+?\.(?:png|jpg|jpeg|webp|gif)(?:\?[^\s<>"')]*)?/gi,
        (url) => pushImage(url, "image"),
      );

      html = html.replace(
        /(?:solution-)?image-[\w.-]+\.(?:png|jpg|jpeg|webp|gif)/gi,
        (imageName) => pushImage(`${imageBaseUrl}/${imageName}`, imageName),
      );

      placeholders.forEach((imageHtml, index) => {
        html = html.replaceAll(`@@QUESTION_IMAGE_${index}@@`, imageHtml);
      });

      return repairMalformedLatex(html.replace(/\n/g, "<br/>"));
    }

    function handleClick() {
      const questionId = getQuestionId(question);

      if (!questionId) {
        console.warn("QUESTION_STATUS_CLICK_ID_MISSING:", {
          index: i,
          question,
        });
        return;
      }

      const nextStatus = getNextStatus(updateState);
      setUpdateState(nextStatus);
      changeStatus(questionId, nextStatus);
    }

    const renderMathHtml = (html, key, isOption = false) => {
      return (
        <MathJax key={key} dynamic hideUntilTypeset="first">
          <div
            dangerouslySetInnerHTML={{
              __html: renderTextWithTags(html, {
                option: isOption,
              }),
            }}
          />
        </MathJax>
      );
    };

    const questionId = getQuestionId(question) || question?.question_num || "q";

    return (
      <div className="pt-2 hover:bg-gray-100">
        <style>{`
          .pdf-option-math mjx-container {
            display: inline-block !important;
            width: auto !important;
            margin: 0 !important;
            text-align: left !important;
            font-size: 100% !important;
          }

          .pdf-option-math mjx-container[display="true"] {
            display: inline-block !important;
            width: auto !important;
            margin: 0 !important;
            text-align: left !important;
          }

          .pdf-option-math > mjx-container,
          .pdf-option-math > div > mjx-container {
            vertical-align: middle !important;
          }
        `}</style>

        <div className="m-2 flex items-end justify-between">
          <div>
            <span className="mr-2 font-semibold">
              {question?.question_num}.
            </span>
          </div>

          <div>
            <Button
              type="button"
              size="sm"
              onClick={handleClick}
              className={
                updateState === "Ok"
                  ? "bg-green-600"
                  : updateState === "Error"
                    ? "bg-red-600"
                    : ""
              }
            >
              {getButtonText(updateState)}
            </Button>
          </div>
        </div>

        <div className="flex justify-between p-3">
          <div className="w-1/2 overflow-x-auto p-1 text-wrap">
            <div className="p-2 text-lg font-semibold">
              {renderMathHtml(
                question?.question_eng,
                `eng-question-${questionId}`,
              )}
            </div>

            <div>
              <ol>
                {(question?.options_eng || []).map((option, index) => (
                  <li
                    className="pdf-option-math p-1"
                    key={`eng-option-${questionId}-${index}`}
                  >
                    {renderMathHtml(
                      option,
                      `eng-option-math-${questionId}-${index}`,
                      true,
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>

          <div className="w-1/2 overflow-x-auto p-1 text-wrap">
            <div className="p-2 text-lg font-semibold">
              {renderMathHtml(
                question?.question_hin,
                `hin-question-${questionId}`,
              )}
            </div>

            <div>
              <ol>
                {(question?.options_hin || []).map((option, index) => (
                  <li
                    className="pdf-option-math p-1"
                    key={`hin-option-${questionId}-${index}`}
                  >
                    {renderMathHtml(
                      option,
                      `hin-option-math-${questionId}-${index}`,
                      true,
                    )}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default Question;

// import React, { memo, useEffect, useMemo, useState } from "react";
// import { MathJax } from "better-react-mathjax";
// import { Button } from "@material-tailwind/react";
// import useAuthStore from "../../../store/authStore";

// const normalizeStatus = (status) => {
//   if (status === "Ok") return "Ok";
//   if (status === "Error") return "Error";
//   return "Unseen";
// };

// const getNextStatus = (status) => {
//   if (status === "Unseen") return "Ok";
//   if (status === "Ok") return "Error";
//   return "Unseen";
// };

// const getButtonText = (status) => {
//   return status === "Unseen" ? "Update" : status;
// };

// const getQuestionId = (question) => {
//   const value = question?.id || question?._id || "";
//   return value ? String(value) : "";
// };

// const OCR_SUPERSCRIPT_MAP = {
//   "⁰": "0",
//   "¹": "1",
//   "²": "2",
//   "³": "3",
//   "⁴": "4",
//   "⁵": "5",
//   "⁶": "6",
//   "⁷": "7",
//   "⁸": "8",
//   "⁹": "9",
//   "⁺": "+",
//   "⁻": "-",
// };

// const replaceOcrSuperscripts = (value) => {
//   return String(value || "").replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻]+/g, (sequence) => {
//     const converted = Array.from(sequence)
//       .map((character) => OCR_SUPERSCRIPT_MAP[character] || "")
//       .join("");

//     return converted ? `^{${converted}}` : sequence;
//   });
// };

// const prefixPlainMathCommand = (value, command) => {
//   const pattern = new RegExp(`\\b${command}\\b`, "gi");

//   return String(value || "").replace(pattern, (match, offset, source) => {
//     const previousCharacter = offset > 0 ? source[offset - 1] : "";

//     return previousCharacter === "\\"
//       ? match
//       : `\\${String(match).toLowerCase()}`;
//   });
// };

// const replacePlainGreekSymbols = (value) => {
//   const greekMap = {
//     μ: "\\mu",
//     α: "\\alpha",
//     β: "\\beta",
//     γ: "\\gamma",
//     δ: "\\delta",
//     θ: "\\theta",
//     φ: "\\phi",
//     π: "\\pi",
//     ω: "\\omega",
//     λ: "\\lambda",
//     ρ: "\\rho",
//     σ: "\\sigma",
//     Δ: "\\Delta",
//   };

//   return String(value || "").replace(
//     /[μαβγδθφπωλρσΔ]/g,
//     (symbol) => greekMap[symbol] || symbol,
//   );
// };

// const normalizePlainFractionsToLatex = (value) => {
//   let text = String(value || "");

//   text = text.replace(
//     /\(\s*([^()\n]+?)\s*\)\s*\/\s*(\\[A-Za-z]+|[A-Za-z0-9.]+)(?=\s*(?:\)|$|[,;]))/g,
//     (match, numerator, denominator) =>
//       `\\frac{${String(numerator).trim()}}{${String(denominator).trim()}}`,
//   );

//   text = text.replace(
//     /(^|[\s(])((?:\\[A-Za-z]+|[A-Za-z0-9.]+))\s*\/\s*((?:\\[A-Za-z]+|[A-Za-z0-9.]+))(?=\s*(?:\)|$|[,;]))/g,
//     (match, prefix, numerator, denominator) =>
//       `${prefix}\\frac{${numerator}}{${denominator}}`,
//   );

//   return text;
// };

// const normalizePlainRootsToLatex = (value) => {
//   let text = String(value || "");

//   text = text.replace(
//     /√\s*\(\s*([^()]+?)\s*\)/g,
//     (match, radicand) => `\\sqrt{${String(radicand).trim()}}`,
//   );

//   text = text.replace(
//     /√\s*([A-Za-z0-9]+|\\[A-Za-z]+)/g,
//     (match, radicand) => `\\sqrt{${String(radicand).trim()}}`,
//   );

//   return text;
// };

// const wrapPlainFunctionArguments = (value) => {
//   return String(value || "").replace(
//     /(\\(?:sin|cos|tan|cot|sec|csc)(?:\^\{[^}]+\})?)\s*\(\s*([^()\n]+(?:\{[^{}]*\}[^()\n]*)*)\s*\)/g,
//     (match, functionName, argument) =>
//       `${functionName}\\left(${String(argument).trim()}\\right)`,
//   );
// };

// const hasMathDelimiter = (value) => {
//   const text = String(value || "");

//   return (
//     text.includes("\\(") ||
//     text.includes("\\[") ||
//     text.includes("$$") ||
//     /(^|[^\\])\$[^$]+\$/.test(text)
//   );
// };

// const looksLikeMath = (value) => {
//   const text = String(value || "");

//   return (
//     /\\(?:frac|dfrac|tfrac|sqrt|sin|cos|tan|cot|sec|csc|log|ln|mu|alpha|beta|gamma|theta|phi|pi|omega|left|right)\b/.test(
//       text,
//     ) ||
//     /\b(?:sin|cos|tan|cot|sec|csc|log|ln)\b/i.test(text) ||
//     /[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻√μαβγδθφπωλρσΔ]/.test(text) ||
//     /\^\s*(?:\{[^}]+\}|[-+]?\d+)/.test(text) ||
//     /_\s*(?:\{[^}]+\}|[A-Za-z0-9])/.test(text) ||
//     /(?:[A-Za-z0-9)]|\})\s*\/\s*(?:[A-Za-z0-9(]|\\)/.test(text)
//   );
// };

// const normalizeOcrMathBodyToLatex = (value) => {
//   let text = String(value || "")
//     .replace(/&mu;/gi, "μ")
//     .replace(/&alpha;/gi, "α")
//     .replace(/&beta;/gi, "β")
//     .replace(/&theta;/gi, "θ")
//     .replace(/&pi;/gi, "π")
//     .replace(/\u2212/g, "-")
//     .replace(/[ \t]+/g, " ")
//     .trim();

//   if (!text || !looksLikeMath(text)) {
//     return text;
//   }

//   text = replaceOcrSuperscripts(text);
//   text = replacePlainGreekSymbols(text);
//   text = normalizePlainRootsToLatex(text);

//   ["sin", "cos", "tan", "cot", "sec", "csc", "log", "ln"].forEach((command) => {
//     text = prefixPlainMathCommand(text, command);
//   });

//   text = normalizePlainFractionsToLatex(text);
//   text = wrapPlainFunctionArguments(text);

//   return text
//     .replace(/\s+\)/g, ")")
//     .replace(/\(\s+/g, "(")
//     .replace(/\s+\}/g, "}")
//     .replace(/\{\s+/g, "{")
//     .replace(/\s{2,}/g, " ")
//     .trim();
// };

// const convertDisplayMathToInline = (value) => {
//   return String(value || "")
//     .replace(
//       /\\\[\s*([\s\S]*?)\s*\\\]/g,
//       (match, formula) => `\\(${String(formula || "").trim()}\\)`,
//     )
//     .replace(
//       /\$\$\s*([\s\S]*?)\s*\$\$/g,
//       (match, formula) => `\\(${String(formula || "").trim()}\\)`,
//     );
// };

// const normalizeOptionForMathJax = (value) => {
//   const rawSource = String(value || "").trim();

//   if (!rawSource) return "";

//   const optionLabelMatch = rawSource.match(
//     /^(\s*(?:\([1-9A-Da-d]\)|\[[1-9A-Da-d]\]|[1-9A-Da-d][.)])\s*)([\s\S]*)$/,
//   );

//   const label = optionLabelMatch ? optionLabelMatch[1] : "";
//   let body = optionLabelMatch ? optionLabelMatch[2].trim() : rawSource;

//   if (!body) return label.trim();

//   // Database/AI may return display delimiters. Options must always be inline.
//   body = convertDisplayMathToInline(body);

//   // Already valid inline math: preserve it and never wrap it again.
//   if (body.includes("\\(") || /(^|[^\\])\$[^$]+\$/.test(body)) {
//     return `${label}${body}`;
//   }

//   // Plain OCR math such as sin⁻¹(1/2) is converted to canonical LaTeX.
//   if (looksLikeMath(body)) {
//     const normalizedBody = normalizeOcrMathBodyToLatex(body);

//     return normalizedBody
//       ? `${label}\\(${normalizedBody}\\)`
//       : `${label}${body}`;
//   }

//   // Non-mathematical options such as "none" remain normal text.
//   return `${label}${body}`;
// };
// const Question = memo(
//   ({ question, i = null, qSetId = null, changeStatus = () => {} }) => {
//     const [updateState, setUpdateState] = useState(
//       normalizeStatus(question?.status),
//     );

//     const { client } = useAuthStore();

//     useEffect(() => {
//       setUpdateState(normalizeStatus(question?.status));
//     }, [question?.status]);

//     const imageBaseUrl = useMemo(() => {
//       const s3Dir = String(client?.s3Dir || "").replace(/^\/+|\/+$/g, "");

//       return `https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${qSetId}`;
//     }, [client?.s3Dir, qSetId]);

//     function renderTextWithTags(text, options = {}) {
//       if (text === null || text === undefined) return "";

//       const source = options.option
//         ? normalizeOptionForMathJax(text)
//         : String(text);

//       return String(source).replace(
//         /(image-[\w.-]+\.png)/g,
//         (match, imageName) => {
//           return `<img src="${imageBaseUrl}/${imageName}" alt="${imageName}" class="inline max-w-full h-auto align-middle" />`;
//         },
//       );
//     }

//     function handleClick() {
//       const questionId = getQuestionId(question);

//       if (!questionId) {
//         console.warn("QUESTION_STATUS_CLICK_ID_MISSING:", {
//           index: i,
//           question,
//         });
//         return;
//       }

//       const nextStatus = getNextStatus(updateState);
//       setUpdateState(nextStatus);
//       changeStatus(questionId, nextStatus);
//     }

//     const renderMathHtml = (html, key, isOption = false) => {
//       return (
//         <MathJax key={key} dynamic hideUntilTypeset="first">
//           <div
//             dangerouslySetInnerHTML={{
//               __html: renderTextWithTags(html, {
//                 option: isOption,
//               }),
//             }}
//           />
//         </MathJax>
//       );
//     };

//     const questionId = getQuestionId(question) || question?.question_num || "q";

//     return (
//       <div className="pt-2 hover:bg-gray-100">
//         <style>{`
//           .pdf-option-math mjx-container {
//             display: inline-block !important;
//             width: auto !important;
//             margin: 0 !important;
//             text-align: left !important;
//             font-size: 100% !important;
//           }

//           .pdf-option-math mjx-container[display="true"] {
//             display: inline-block !important;
//             width: auto !important;
//             margin: 0 !important;
//             text-align: left !important;
//           }

//           .pdf-option-math > mjx-container,
//           .pdf-option-math > div > mjx-container {
//             vertical-align: middle !important;
//           }
//         `}</style>
//         <div className="m-2 flex items-end justify-between">
//           <div>
//             <span className="mr-2 font-semibold">
//               {question?.question_num}.
//             </span>
//           </div>

//           <div>
//             <Button
//               type="button"
//               size="sm"
//               onClick={handleClick}
//               className={
//                 updateState === "Ok"
//                   ? "bg-green-600"
//                   : updateState === "Error"
//                     ? "bg-red-600"
//                     : ""
//               }
//             >
//               {getButtonText(updateState)}
//             </Button>
//           </div>
//         </div>

//         <div className="flex justify-between p-3">
//           <div className="w-1/2 overflow-x-auto p-1 text-wrap">
//             <div className="p-2 text-lg font-semibold">
//               {renderMathHtml(
//                 question?.question_eng,
//                 `eng-question-${questionId}`,
//               )}
//             </div>

//             <div>
//               <ol>
//                 {(question?.options_eng || []).map((option, index) => (
//                   <li
//                     className="pdf-option-math p-1"
//                     key={`eng-option-${questionId}-${index}`}
//                   >
//                     {renderMathHtml(
//                       option,
//                       `eng-option-math-${questionId}-${index}`,
//                       true,
//                     )}
//                   </li>
//                 ))}
//               </ol>
//             </div>
//           </div>

//           <div className="w-1/2 overflow-x-auto p-1 text-wrap">
//             <div className="p-2 text-lg font-semibold">
//               {renderMathHtml(
//                 question?.question_hin,
//                 `hin-question-${questionId}`,
//               )}
//             </div>

//             <div>
//               <ol>
//                 {(question?.options_hin || []).map((option, index) => (
//                   <li
//                     className="pdf-option-math p-1"
//                     key={`hin-option-${questionId}-${index}`}
//                   >
//                     {renderMathHtml(
//                       option,
//                       `hin-option-math-${questionId}-${index}`,
//                       true,
//                     )}
//                   </li>
//                 ))}
//               </ol>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   },
// );

// export default Question;

// import React, { memo, useEffect, useState } from "react";
// import { MathJax } from "better-react-mathjax";
// import { Button } from "@material-tailwind/react";
// import useAuthStore from "../../../store/authStore";

// const normalizeStatus = (status) => {
//   if (status === "Ok") return "Ok";
//   if (status === "Error") return "Error";
//   return "Unseen";
// };

// const getNextStatus = (status) => {
//   if (status === "Unseen") return "Ok";
//   if (status === "Ok") return "Error";
//   return "Unseen";
// };

// const getButtonText = (status) => {
//   return status === "Unseen" ? "Update" : status;
// };

// const getQuestionId = (question) => {
//   const value = question?.id || question?._id || "";
//   return value ? String(value) : "";
// };

// const Question = memo(
//   ({ question, i = null, qSetId = null, changeStatus = () => {} }) => {
//     const [updateState, setUpdateState] = useState(
//       normalizeStatus(question?.status),
//     );

//     const { client } = useAuthStore();

//     /*
//      * Parent se DB/refetch ke baad status change aaye to
//      * local button state ko sync rakho.
//      */
//     useEffect(() => {
//       setUpdateState(normalizeStatus(question?.status));
//     }, [question?.status]);

//     function renderTextWithTags(text) {
//       if (text === null || text === undefined) return "";

//       const s3Dir = client?.s3Dir || "";

//       return String(text).replace(
//         /(image-[\w.-]+\.png)/g,
//         (match, imageName) => {
//           return `<img src="https://s3.us-west-004.backblazeb2.com/s3file/${s3Dir}/QSet/${qSetId}/${imageName}" alt="${imageName}" class="inline max-w-full h-auto" />`;
//         },
//       );
//     }

//     function handleClick() {
//       const questionId = getQuestionId(question);

//       if (!questionId) {
//         console.warn("QUESTION_STATUS_CLICK_ID_MISSING:", {
//           index: i,
//           question,
//         });
//         return;
//       }

//       const nextStatus = getNextStatus(updateState);

//       /*
//        * Important:
//        * Array index `i` nahi, actual Mongo question id parent ko bhejna hai.
//        */
//       setUpdateState(nextStatus);
//       changeStatus(questionId, nextStatus);
//     }

//     return (
//       <MathJax dynamic>
//         <div className="pt-2 hover:bg-gray-100">
//           <div className="m-2 flex items-end justify-between">
//             <div>
//               <span className="mr-2 font-semibold">
//                 {question?.question_num}.
//               </span>
//             </div>

//             <div>
//               <Button
//                 type="button"
//                 size="sm"
//                 onClick={handleClick}
//                 className={
//                   updateState === "Ok"
//                     ? "bg-green-600"
//                     : updateState === "Error"
//                       ? "bg-red-600"
//                       : ""
//                 }
//               >
//                 {getButtonText(updateState)}
//               </Button>
//             </div>
//           </div>

//           <div className="flex justify-between p-3">
//             <div className="w-1/2 overflow-x-auto p-1 text-wrap">
//               <div className="p-2 text-lg font-semibold">
//                 <div
//                   dangerouslySetInnerHTML={{
//                     __html: renderTextWithTags(question?.question_eng),
//                   }}
//                 />
//               </div>

//               <div>
//                 <ol>
//                   {(question?.options_eng || []).map((option, index) => (
//                     <li
//                       className="p-1"
//                       key={`eng-option-${
//                         getQuestionId(question) || question?.question_num || "q"
//                       }-${index}`}
//                       dangerouslySetInnerHTML={{
//                         __html: renderTextWithTags(option),
//                       }}
//                     />
//                   ))}
//                 </ol>
//               </div>
//             </div>

//             <div className="w-1/2 overflow-x-auto p-1 text-wrap">
//               <div className="p-2 text-lg font-semibold">
//                 <div
//                   dangerouslySetInnerHTML={{
//                     __html: renderTextWithTags(question?.question_hin),
//                   }}
//                 />
//               </div>

//               <div>
//                 <ol>
//                   {(question?.options_hin || []).map((option, index) => (
//                     <li
//                       className="p-1"
//                       key={`hin-option-${
//                         getQuestionId(question) || question?.question_num || "q"
//                       }-${index}`}
//                       dangerouslySetInnerHTML={{
//                         __html: renderTextWithTags(option),
//                       }}
//                     />
//                   ))}
//                 </ol>
//               </div>
//             </div>
//           </div>
//         </div>
//       </MathJax>
//     );
//   },
// );

// export default Question;

// import React, { memo, useEffect, useState } from "react";
// import { MathJax } from "better-react-mathjax";
// import { Button } from "@material-tailwind/react";

// const Question = memo(
//   ({ question, i = null, qSetId = null, changeStatus = () => {} }) => {
//     const [updateState, setUpdateState] = useState(question.status || "Update");

//     function renderTextWithTags(text) {
//       if (text === null || text === undefined) return;
//       return text.replace(/(image-[\w.-]+\.png)/g, (match, imageName) => {
//         // return `<img src="https://s3file.s3.us-west-004.backblazeb2.com/1demo/QSet/${qSetId}/${imageName}" alt="${imageName}" class="inline max-w-full h-auto " />`;
//         return `<img src="https://s3.us-west-004.backblazeb2.com/s3file/435abcschool/QSet/${qSetId}/${imageName}" alt="${imageName}" class="inline max-w-full h-auto " />`;
//       });
//       //return text
//     }
//     useEffect(() => {
//       if (updateState === "Ok") {
//         changeStatus(i, "Ok");
//       } else if (updateState === "Error") {
//         changeStatus(i, "Error");
//       } else {
//         changeStatus(i, "update");
//       }
//     }, [updateState]);

//     function handleClick() {
//       if (updateState === "Update") {
//         setUpdateState("Ok");
//         // changeStatus(i,'Ok')
//         return;
//       } else if (updateState === "Ok") {
//         setUpdateState("Error");
//         // changeStatus(i,'Error')
//         return;
//       } else {
//         setUpdateState("Update");
//         // changeStatus(i,'')
//         return;
//       }
//     }

//     return (
//       <div>
//         <div className="m-2 flex justify-between items-end">
//           <div>
//             <span className="font-semibold mr-2">
//               {question.question_num} .
//             </span>
//           </div>

//           <div>
//             <Button
//               size="sm"
//               onClick={handleClick}
//               className={
//                 updateState === "Ok"
//                   ? "bg-green-600"
//                   : updateState === "Error"
//                     ? "bg-red-600"
//                     : ""
//               }
//             >
//               {updateState}
//             </Button>
//           </div>
//         </div>

//         <div className={`flex justify-between p-3  `}>
//           {/* English Question Section*/}
//           <div className="w-1/2 text-wrap p-1 overflow-x-auto">
//             <div className="p-2 text-lg font-semibold">
//               {/* <p dangerouslySetInnerHTML={{__html:question.question_eng}}>{renderTextWithTags(question.question_eng)}</p> */}
//               <p
//                 dangerouslySetInnerHTML={{
//                   __html: renderTextWithTags(question.question_eng),
//                 }}
//               ></p>
//             </div>

//             <div>
//               <ol>
//                 {question["options_eng"].map((option, i) => (
//                   <li
//                     className="p-1 "
//                     key={i}
//                     dangerouslySetInnerHTML={{
//                       __html: renderTextWithTags(option),
//                     }}
//                   >
//                     {/* <span className="mr-2">({i + 1})</span> */}
//                     {/* {renderTextWithTags(option)}  */}
//                   </li>
//                 ))}
//               </ol>
//             </div>
//           </div>

//           {/* Hindi Question Section*/}
//           <div className="w-1/2 text-wrap p-1 overflow-x-auto">
//             <div className="p-2 text-lg font-semibold">
//               {/* <span className="font-semibold mr-2">{question.question_num} .</span> */}
//               {/* {renderTextWithTags(question.question_hin)} */}
//               <p
//                 dangerouslySetInnerHTML={{
//                   __html: renderTextWithTags(question.question_hin),
//                 }}
//               ></p>
//             </div>

//             <div>
//               <ol>
//                 {question["options_hin"].map((option, i) => (
//                   <li
//                     className="p-1 "
//                     key={i}
//                     dangerouslySetInnerHTML={{
//                       __html: renderTextWithTags(option),
//                     }}
//                   >
//                     {/* <span className="mr-2">({i + 1})</span> */}
//                     {/* {renderTextWithTags(option)}  */}
//                   </li>
//                 ))}
//               </ol>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   },
// );

// export default Question;
